import { useCallback } from 'react';
import first from 'lodash/first';
import { useProject } from '@atlassian/jira-business-entity-project-hook/src/index.tsx';
import { useIssueTypesAndFields } from '@atlassian/jira-business-entity-project/src/services/issue-types-and-fields/index.tsx';
import { useFilteredViewContextInput } from '@atlassian/jira-business-filters/src/controllers/default-query-input/index.tsx';
import { isOptimisticIssue } from '@atlassian/jira-business-issue-create/src/controllers/issue-create-context/index.tsx';
import type { OptimisticIssuePayload } from '@atlassian/jira-business-issue-create/src/types.tsx';
import { useCacheActions } from '@atlassian/jira-business-list-handlers/src/controllers/list-cache/main.tsx';
import { useOverview } from '@atlassian/jira-router-resources-business-overview-details/src/index.tsx';
import { fieldIds } from '../../common/constants.tsx';
import { getVariables } from '../../common/utils/get-variables/index.tsx';
import { useIssueParentMap } from '../../common/utils/use-issue-parent-map.tsx';
import { createOptimisticIssue } from '../../services/create-issue/utils.tsx';
import type {
	JWMTimelineViewItems,
	JWMTimelineViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node as JWMTimelineViewItemsNode,
} from '../../services/get-timeline-view-issues/__generated_apollo__/JWMTimelineViewItems';
import { TIMELINE_VIEW_ITEMS_QUERY } from '../../services/get-timeline-view-issues/gql.tsx';
import { addChildToParentTimeline } from '../../services/get-timeline-view-issues/utils.tsx';

export const useOptimisticIssueCreate = () => {
	const { quickFiltersJql, hierarchyParent, jqlContext, isHidingDoneItems } =
		useFilteredViewContextInput({
			view: 'timeline',
		});

	const { hierarchyLevels } = useIssueTypesAndFields({ issueOperation: 'VIEW' });
	const hierarchyLevel = first(hierarchyLevels)?.level;

	const [{ issueParentMap }] = useIssueParentMap();
	const { updateItemsInCache, addToCache, removeFromCache } = useCacheActions();

	const { data: overview } = useOverview();
	const isOverview = overview != null && Array.isArray(overview.projects);

	let projectKey: string | undefined;
	// useProject is not available in the overviews
	if (!isOverview) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const { key } = useProject();
		projectKey = key;
	}

	const getUpdateVariables = useCallback(
		(parentId: string) => {
			const grandparentIssueId = issueParentMap.get(parentId);
			const baseVariables = {
				jqlContext,
				fieldIds,
				quickFiltersJql,
				isHidingDoneItems,
				...(projectKey ? { projectIdOrKey: projectKey } : {}),
			};

			if (grandparentIssueId && hierarchyParent) {
				return getVariables({
					...baseVariables,
					hierarchyParent: undefined,
					parentIssue: {
						hierarchyType: hierarchyParent,
						issueId: grandparentIssueId,
					},
				});
			}

			return getVariables({
				...baseVariables,
				hierarchyLevel,
				hierarchyParent,
			});
		},
		[
			hierarchyLevel,
			hierarchyParent,
			isHidingDoneItems,
			issueParentMap,
			jqlContext,
			projectKey,
			quickFiltersJql,
		],
	);

	// Update the cache with the newly created issue, replacing any optimistic issue if present
	const updateIssue = useCallback(
		(item: JWMTimelineViewItemsNode, parentId?: string) => {
			// handle optimistic update for children item if the hierarchy feature is enabled
			if (parentId != null && hierarchyParent != null) {
				// update the parent issue to contain the new subtask so it will be shown in the UI correctly
				if (isOptimisticIssue(item.issueId)) {
					updateItemsInCache<JWMTimelineViewItemsNode, JWMTimelineViewItems>({
						query: TIMELINE_VIEW_ITEMS_QUERY,
						variables: getUpdateVariables(parentId),
						issueIds: [parentId],
						updateItem: addChildToParentTimeline,
					});
				}

				// update the children cache to add the new subtask
				addToCache<JWMTimelineViewItemsNode, JWMTimelineViewItems>({
					newNodes: [item],
					query: TIMELINE_VIEW_ITEMS_QUERY,
					variables: getVariables({
						jqlContext,
						fieldIds,
						quickFiltersJql,
						isHidingDoneItems,
						hierarchyParent: undefined,
						parentIssue: {
							hierarchyType: hierarchyParent,
							issueId: parentId,
						},
						...(projectKey ? { projectIdOrKey: projectKey } : {}),
					}),
				});

				return;
			}

			addToCache({
				newNodes: [item],
				query: TIMELINE_VIEW_ITEMS_QUERY,
				variables: {
					...getVariables({
						jqlContext,
						fieldIds,
						quickFiltersJql,
						isHidingDoneItems,
						hierarchyParent,
						hierarchyLevel,
						...(projectKey ? { projectIdOrKey: projectKey } : {}),
					}),
				},
			});
		},
		[
			hierarchyParent,
			addToCache,
			jqlContext,
			quickFiltersJql,
			isHidingDoneItems,
			hierarchyLevel,
			projectKey,
			updateItemsInCache,
			getUpdateVariables,
		],
	);

	// Add an optimistic issue to the cache
	const addOptimisticIssue = useCallback(
		(issue: OptimisticIssuePayload, parentId?: string) => {
			const optimisticItem = createOptimisticIssue(issue);
			updateIssue(optimisticItem, parentId);
		},
		[updateIssue],
	);

	const removeOptimisticIssue = useCallback(
		(issueId: number, parentId?: string) => {
			let variables;

			if (parentId != null && hierarchyParent != null) {
				variables = getVariables({
					jqlContext,
					fieldIds,
					quickFiltersJql,
					isHidingDoneItems,
					hierarchyParent: undefined,
					parentIssue: {
						hierarchyType: hierarchyParent,
						issueId: parentId,
					},
					...(projectKey ? { projectIdOrKey: projectKey } : {}),
				});
			} else {
				variables = getVariables({
					jqlContext,
					fieldIds,
					quickFiltersJql,
					isHidingDoneItems,
					hierarchyParent,
					hierarchyLevel,
					...(projectKey ? { projectIdOrKey: projectKey } : {}),
				});
			}

			removeFromCache({ query: TIMELINE_VIEW_ITEMS_QUERY, variables, issueId });
		},
		[
			hierarchyParent,
			removeFromCache,
			jqlContext,
			quickFiltersJql,
			isHidingDoneItems,
			projectKey,
			hierarchyLevel,
		],
	);

	return { addOptimisticIssue, updateIssue, removeOptimisticIssue };
};
